"use client";

import createGlobe, { COBEOptions } from "cobe";
import { useMotionValue, useSpring } from "motion/react";
import { useEffect, useRef } from "react";

import { cn } from "@/lib/utils";

const MOVEMENT_DAMPING = 1400;

export const GLOBE_CONFIG: COBEOptions = {
  width: 800,
  height: 800,
  onRender: () => {},
  devicePixelRatio: 2,
  phi: 0,
  theta: 0.3,
  dark: 0,
  diffuse: 0.4,
  mapSamples: 16000,
  mapBrightness: 1.2,
  baseColor: [1, 1, 1],
  markerColor: [251 / 255, 44 / 255, 54 / 255],
  glowColor: [1, 1, 1],
  markers: [
    { location: [14.5995, 120.9842], size: 0.03 },
    { location: [19.076, 72.8777], size: 0.1 },
    { location: [23.8103, 90.4125], size: 0.05 },
    { location: [30.0444, 31.2357], size: 0.07 },
    { location: [39.9042, 116.4074], size: 0.08 },
    { location: [-23.5505, -46.6333], size: 0.1 },
    { location: [19.4326, -99.1332], size: 0.1 },
    { location: [40.7128, -74.006], size: 0.1 },
    { location: [34.6937, 135.5022], size: 0.05 },
    { location: [41.0082, 28.9784], size: 0.06 },
  ],
};


const VARIANT_CONFIGS: Record<string, Partial<COBEOptions>> = {
  'Light A': { 
    phi: 1.74,
    theta: -0.1,
    mapSamples: 15000,
    mapBrightness: 12,
    mapBaseBrightness: 0.03,
    diffuse: 1.4,
    dark: 0,
    scale: 1,
    opacity: 1,
    baseColor: [1, 1, 1], 
    glowColor: [1, 1, 1],
    markerColor: [251/255, 44/255, 54/255],
    markers: [
      { location: [14.5995, 120.9842], size: 0.03 },
      { location: [19.076, 72.8777], size: 0.03 },
      { location: [23.8103, 90.4125], size: 0.03 },
      { location: [30.0444, 31.2357], size: 0.03 },
      { location: [39.9042, 116.4074], size: 0.03 },
      { location: [-23.5505, -46.6333], size: 0.03 },
      { location: [19.4326, -99.1332], size: 0.03 },
      { location: [40.7128, -74.006], size: 0.03 },
      { location: [34.6937, 135.5022], size: 0.03 },
      { location: [41.0082, 28.9784], size: 0.03 },
    ],
  },
  'Light B': { 
    phi: 1.74,
    theta: -0.1,
    mapSamples: 15000,
    mapBrightness: 12,
    mapBaseBrightness: 0.03,
    diffuse: 0,
    dark: 0,
    scale: 1,
    opacity: 1,
    baseColor: [1, 1, 1], 
    glowColor: [235/255, 235/255, 235/255],
    markerColor: [251/255, 44/255, 54/255],
    markers: [
      { location: [14.5995, 120.9842], size: 0.03 },
      { location: [19.076, 72.8777], size: 0.03 },
      { location: [23.8103, 90.4125], size: 0.03 },
      { location: [30.0444, 31.2357], size: 0.03 },
      { location: [39.9042, 116.4074], size: 0.03 },
      { location: [-23.5505, -46.6333], size: 0.03 },
      { location: [19.4326, -99.1332], size: 0.03 },
      { location: [40.7128, -74.006], size: 0.03 },
      { location: [34.6937, 135.5022], size: 0.03 },
      { location: [41.0082, 28.9784], size: 0.03 },
    ],
  },
  'Dark A': {
    phi: 1.74,
    theta: -0.1,
    mapSamples: 15000,
    mapBrightness: 12,
    mapBaseBrightness: 0.07,
    diffuse: 0,
    dark: 0.83,
    scale: 1,
    opacity: 1,
    baseColor: [30/255, 40/255, 62/255], 
    glowColor: [13/255, 18/255, 29/255],
    markerColor: [251/255, 44/255, 54/255],
    markers: [
      { location: [14.5995, 120.9842], size: 0.03 },
      { location: [19.076, 72.8777], size: 0.03 },
      { location: [23.8103, 90.4125], size: 0.03 },
      { location: [30.0444, 31.2357], size: 0.03 },
      { location: [39.9042, 116.4074], size: 0.03 },
      { location: [-23.5505, -46.6333], size: 0.03 },
      { location: [19.4326, -99.1332], size: 0.03 },
      { location: [40.7128, -74.006], size: 0.03 },
      { location: [34.6937, 135.5022], size: 0.03 },
      { location: [41.0082, 28.9784], size: 0.03 },
    ],
  },
  'Dark B': { 
    phi: 1.74,
    theta: -0.1,
    mapSamples: 15000,
    mapBrightness: 12,
    mapBaseBrightness: 0.07,
    diffuse: 0,
    dark: 0.72,
    scale: 1,
    opacity: 1,
    baseColor: [17/255, 24/255, 39/255], 
    glowColor: [17/255, 24/255, 39/255], 
    markerColor: [251/255, 44/255, 54/255],
    markers: [
      { location: [14.5995, 120.9842], size: 0.03 },
      { location: [19.076, 72.8777], size: 0.03 },
      { location: [23.8103, 90.4125], size: 0.03 },
      { location: [30.0444, 31.2357], size: 0.03 },
      { location: [39.9042, 116.4074], size: 0.03 },
      { location: [-23.5505, -46.6333], size: 0.03 },
      { location: [19.4326, -99.1332], size: 0.03 },
      { location: [40.7128, -74.006], size: 0.03 },
      { location: [34.6937, 135.5022], size: 0.03 },
      { location: [41.0082, 28.9784], size: 0.03 },
    ],
  },
};

const Variants_Config = (darkMode: boolean, variant: string, revert: boolean): COBEOptions => {
  // If revert is true, switch A to B and B to A
  const actualVariant = revert ? (variant === 'A' ? 'B' : 'A') : variant;
  const key = `${darkMode ? 'Dark' : 'Light'} ${actualVariant}`;
  const config = VARIANT_CONFIGS[key];
  
  return config ? { ...GLOBE_CONFIG, ...config } : GLOBE_CONFIG;
};



export function Globe({
  className,
  config,
  darkMode,
  variant,
  revert = false,
}: {
  className?: string;
  config?: COBEOptions;
  darkMode: boolean;
  variant: string;
  revert?: boolean;
}) {
  let phi = 0;
  let width = 0;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pointerInteracting = useRef<number | null>(null);
  const pointerInteractionMovement = useRef(0);

  const r = useMotionValue(0);
  const rs = useSpring(r, {
    mass: 1,
    damping: 30,
    stiffness: 100,
  });

  const updatePointerInteraction = (value: number | null) => {
    pointerInteracting.current = value;
    if (canvasRef.current) {
      canvasRef.current.style.cursor = value !== null ? "grabbing" : "grab";
    }
  };

  const updateMovement = (clientX: number) => {
    if (pointerInteracting.current !== null) {
      const delta = clientX - pointerInteracting.current;
      pointerInteractionMovement.current = delta;
      r.set(r.get() + delta / MOVEMENT_DAMPING);
    }
  };

  useEffect(() => {
    const onResize = () => {
      if (canvasRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        width = canvasRef.current.offsetWidth;
      }
    };

    window.addEventListener("resize", onResize);
    onResize();

    const variant_config = Variants_Config(darkMode, variant, revert);

    const globe = createGlobe(canvasRef.current!, {
      ...variant_config,
      width: width * 2,
      height: width * 2,
      onRender: (state) => {
        if (!pointerInteracting.current) {
          // eslint-disable-next-line react-hooks/exhaustive-deps
          phi += 0.005;
        }
        state.phi = phi + rs.get();
        state.width = width * 2;
        state.height = width * 2;
      },
    });

    setTimeout(() => (canvasRef.current!.style.opacity = "1"), 0);
    return () => {
      globe.destroy();
      window.removeEventListener("resize", onResize);
    };
  }, [rs, config, darkMode, variant, revert]);

  return (
    <div
      className={cn(
        "absolute inset-0 mx-auto aspect-[1/1] w-full max-w-[600px]",
        className,
      )}
    >
      <canvas
        className={cn(
          "size-full opacity-0 transition-opacity duration-500 [contain:layout_paint_size]",
        )}
        ref={canvasRef}
        onPointerDown={(e) => {
          pointerInteracting.current = e.clientX;
          updatePointerInteraction(e.clientX);
        }}
        onPointerUp={() => updatePointerInteraction(null)}
        onPointerOut={() => updatePointerInteraction(null)}
        onMouseMove={(e) => updateMovement(e.clientX)}
        onTouchMove={(e) =>
          e.touches[0] && updateMovement(e.touches[0].clientX)
        }
      />
    </div>
  );
}
